import { isFilled } from "./is"

export const capitalize = (str?: string | null) => {
  if (!str) return str
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
}

export const capitalizeAll = (sentence?: string) => {
  if (!sentence) return sentence
  return sentence
    .split(" ")
    .map((word) => capitalize(word))
    .join(" ")
}

export const normalizeStr = (str?: string | null) =>
  (str ?? "")
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim()

export const sanitizeUnicode = (str?: string | null): string | null | undefined => {
  if (str == null || str === "") return str

  // 1. Remove null characters (\u0000 / \x00) which PostgreSQL text fields cannot store
  str = str.replace(/\u0000/g, "")

  // 2. Use Native Method (Node 20+ / Modern Browsers)
  // This is the fastest and most reliable way if supported.
  if (typeof str.toWellFormed === "function") {
    return str.toWellFormed()
  }

  const len = str.length
  const result: string[] = []

  for (let i = 0; i < len; i++) {
    const charCode = str.charCodeAt(i)
    const strAtIndex = str[i]
    const strAtNextIndex = str[i + 1]

    if (!strAtIndex) continue

    // High Surrogate (0xD800 – 0xDBFF)
    if (charCode >= 0xd800 && charCode <= 0xdbff) {
      if (i + 1 < len) {
        const nextCode = str.charCodeAt(i + 1)
        // If followed by valid Low Surrogate (0xDC00 – 0xDFFF)
        if (nextCode >= 0xdc00 && nextCode <= 0xdfff && strAtNextIndex) {
          result.push(strAtIndex, strAtNextIndex)
          i++ // Skip the low surrogate as it's been consumed
          continue
        }
      }
      // Lone high surrogate: replace with U+FFFD
      result.push("\uFFFD")
    }
    // Lone Low Surrogate (0xDC00 – 0xDFFF)
    // If we hit this, it wasn't preceded by a high surrogate
    else if (charCode >= 0xdc00 && charCode <= 0xdfff) {
      result.push("\uFFFD")
    }
    // Normal character
    else {
      result.push(strAtIndex)
    }
  }

  return result.join("")
}

export const sanitizeJsonValue = <T>(value: T): T => {
  if (value == null) return value

  if (typeof value === "string") {
    return sanitizeUnicode(value) as T
  }

  if (Array.isArray(value)) {
    return value.map((item) => sanitizeJsonValue(item)) as T
  }

  if (typeof value === "object") {
    const sanitized: Record<string, unknown> = {}
    for (const key in value) {
      sanitized[key] = sanitizeJsonValue((value as Record<string, unknown>)[key])
    }
    return sanitized as T
  }

  return value
}

export const truncateString = (
  str: string | null | undefined,
  maxLength: number,
): string | null => {
  if (str == null || str === "") return str ?? null
  if (maxLength < 0) return str
  if (str.length <= maxLength) return str

  let truncated = str.slice(0, maxLength)

  const lastCharCode = truncated.charCodeAt(truncated.length - 1)
  if (lastCharCode >= 0xd800 && lastCharCode <= 0xdbff) {
    truncated = truncated.slice(0, -1)
  }

  return truncated
}

export function joinName(
  firstName: string | null | undefined,
  lastName: string | null | undefined,
) {
  return [firstName, lastName].filter(isFilled).join(" ") || null
}
